import * as React from 'react';
import {styled, alpha, createTheme} from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import Badge from '@mui/material/Badge';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircle from '@mui/icons-material/AccountCircle';
import MailIcon from '@mui/icons-material/Mail';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Avatar from '@mui/material/Avatar';
import MoreIcon from '@mui/icons-material/MoreVert';
import { ThemeProvider  } from '@mui/material/styles';
import logo from "../assets/images/logo/sparheel_logo.png";
import {useAuth0} from "@auth0/auth0-react";
import {NavLink} from "react-router-dom";
import HomeIcon from '@mui/icons-material/Home';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import algoliasearch from "algoliasearch";
import {InstantSearch, SearchBox, Hits, Highlight, Pagination, RefinementList} from 'react-instantsearch-hooks-web'
import 'instantsearch.css/themes/satellite.css';
import {autocomplete, getAlgoliaResults} from '@algolia/autocomplete-js';
import '@algolia/autocomplete-theme-classic';
import {Autocomplete} from "./Autocomplete";
import {ProductItem} from "./ProductItem";



const theme = createTheme({
    palette: {
        appBar:{
            main:'#fff',
            contrastText: '#800020',
        }

    },
});

const Search = styled('div')(({ theme }) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.black, 0.05),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.black, 0.15),
    },
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(3),
        width: 'auto',
    },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
    color: 'inherit',
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        // vertical padding + font size from searchIcon
        paddingLeft: `calc(1em + ${theme.spacing(4)})`,
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('md')]: {
            width: '20ch',
        },
    },
}));

export default function AppBarView(props) {
    const {logout} = useAuth0();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState(null);

    const isMenuOpen = Boolean(anchorEl);
    const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

    const handleProfileMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMobileMenuClose = () => {
        setMobileMoreAnchorEl(null);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        handleMobileMenuClose();
    };

    const handleMobileMenuOpen = (event) => {
        setMobileMoreAnchorEl(event.currentTarget);
    };

    const menuId = 'primary-search-account-menu';
    const renderMenu = (
        <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            id={menuId}
            keepMounted
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            open={isMenuOpen}
            onClose={handleMenuClose}
        >
            <MenuItem component={NavLink} to="/profile">View profile</MenuItem>
            <MenuItem component={NavLink} to="/settings">Settings</MenuItem>
            <MenuItem component={NavLink} to={"/learning"}>Learning Portal</MenuItem>
            <MenuItem onClick={handleMenuClose}>Support</MenuItem>
            <MenuItem onClick={()=>logout({returnTo:"https://about.sparheel.com"})}>Sign Out</MenuItem>
        </Menu>
    );

    const mobileMenuId = 'primary-search-account-menu-mobile';
    const renderMobileMenu = (
        <Menu
            anchorEl={mobileMoreAnchorEl}
            anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            id={mobileMenuId}
            keepMounted
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
            open={isMobileMenuOpen}
            onClose={handleMobileMenuClose}
        >
            <MenuItem component={NavLink} to="/">
                <IconButton size="large" aria-label="show 4 new mails" color="inherit">
                    <HomeIcon />
                </IconButton>
                <p>Home</p>
            </MenuItem>
            <MenuItem>
                <IconButton
                    size="large"
                    aria-label="show 4 new mails"
                    color="inherit"
                    component={NavLink} to="/circle"
                >
                    <Badge badgeContent={""} color="error">
                        <PeopleAltIcon />
                    </Badge>
                </IconButton>
                <p>Connections</p>
            </MenuItem>
            <MenuItem>
                <IconButton
                    size="large"
                    aria-label="show 17 new notifications"
                    color="inherit"
                    component={NavLink} to="/notifications"
                >
                    <Badge badgeContent={props.notification} color="error">
                        <NotificationsIcon />
                    </Badge>
                </IconButton>
                <p>Notifications</p>
            </MenuItem>
            <MenuItem onClick={handleProfileMenuOpen}>
                <IconButton
                    size="large"
                    aria-label="account of current user"
                    aria-controls="primary-search-account-menu"
                    aria-haspopup="true"
                    color="inherit"
                >
                    <Avatar alt="profile picture" src={props.profile.avatar} />
                </IconButton>
                <p>Profile</p>
            </MenuItem>
        </Menu>
    );
    const searchClient = algoliasearch(process.env.REACT_APP_ALGOLIA_ID,process.env.REACT_APP_ALGOLIA_API)
    return (
        <InstantSearch indexName={"user_index"} searchClient={searchClient}>
            <Box sx={{ flexGrow: 1 }}>
                <ThemeProvider theme={theme}>
                    <AppBar position="static" color="appBar">
                        <Toolbar>
                            <IconButton
                                size="large"
                                edge="start"
                                color="inherit"
                                aria-label="open drawer"
                                sx={{ mr: 2 }}
                                component={NavLink} to="/"
                            >
                                <img src={logo} alt="logo" height="30"></img>
                            </IconButton>
                            <Typography
                                variant="h6"
                                noWrap
                                component="div"
                                sx={{ display: { xs: 'none', sm: 'block' },mr:1 }}
                            >
                                SPARHEEL
                            </Typography>
                            <Autocomplete
                                openOnFocus={true}
                                getSources={({ query }) => [
                                {
                                    sourceId: 'users',
                                    getItems() {
                                    return getAlgoliaResults({
                                        searchClient,
                                        queries: [
                                            {
                                                indexName: 'user_index',
                                                query,
                                            },
                                        ],
                                    });
                                },
                                templates: {
                                    item({ item, components }) {
                                        return <ProductItem hit={item} components={components} />;
                                    },
                                },
                            },
                            ]}
                            />
                            <Box sx={{ flexGrow: 1 }} />
                            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                                <IconButton size="large" aria-label="show 4 new mails" color="inherit" component={NavLink} to="/">
                                    <HomeIcon />
                                </IconButton>
                                <IconButton
                                    size="large"
                                    aria-label="show connections"
                                    color="inherit"
                                    component={NavLink} to="/circle"
                                >
                                    <PeopleAltIcon />
                                </IconButton>
                                <IconButton
                                    size="large"
                                    aria-label="show new notifications"
                                    color="inherit"
                                    component={NavLink} to="/notifications"
                                >
                                    <Badge badgeContent={props.notification} color="error">
                                        <NotificationsIcon />
                                    </Badge>
                                </IconButton>

                                <IconButton
                                    size="large"
                                    edge="end"
                                    aria-label="account of current user"
                                    aria-controls={menuId}
                                    aria-haspopup="true"
                                    onClick={handleProfileMenuOpen}
                                    color="inherit"
                                >
                                    <Avatar alt="profile picture" src={props.profile.avatar} />
                                </IconButton>
                            </Box>
                            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                                <IconButton
                                    size="large"
                                    aria-label="show more"
                                    aria-controls={mobileMenuId}
                                    aria-haspopup="true"
                                    onClick={handleMobileMenuOpen}
                                    color="inherit"
                                >
                                    <MoreIcon/>
                                </IconButton>
                            </Box>
                        </Toolbar>
                    </AppBar>
                </ThemeProvider>
                {renderMobileMenu}
                {renderMenu}
            </Box>
        </InstantSearch>
    );
}